import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Stack, alpha, useTheme
} from '@mui/material';
import {
  People, AttachMoney, TrendingUp, TrendingDown,
  AccountBalance, PhoneAndroid, Money, School
} from '@mui/icons-material';
import { formatCurrency, parseNumber, calculatePercentage } from '../utils/formatters';

// ─── Animated counter hook ───────────────────────────────────────────────────
function useCountUp(target, duration = 1200, decimals = 0) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const to = parseFloat(target) || 0;
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setValue(parseFloat((from + (to - from) * ease).toFixed(decimals)));
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration, decimals]);
  return value;
}

// ─── Single Stat Card ─────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color, delay = 0, format = 'number' }) => {
  const animated = useCountUp(value, 1400, format === 'currency' ? 0 : format === 'percent' ? 1 : 0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const display =
    format === 'currency'
      ? `UGX ${animated.toLocaleString()}`
      : format === 'percent'
      ? `${animated}%`
      : animated.toLocaleString();

  return (
    <Box
      sx={{
        flex: '1 1 220px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: '18px 20px',
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.04)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          position: 'relative',
          overflow: 'hidden',
          transition: 'border-color 0.3s, box-shadow 0.3s',
          '&:hover': {
            borderColor: alpha(color, 0.5),
            boxShadow: `0 4px 24px ${alpha(color, 0.15)}`,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '15%',
            bottom: '15%',
            width: '3px',
            borderRadius: '0 3px 3px 0',
            background: color,
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            background: alpha(color, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 22, color }} />
        </Box>
        <Stack spacing={0.25}>
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Syne", sans-serif',
              fontSize: '1.05rem',
              fontWeight: 700,
              color,
              letterSpacing: '-0.01em',
            }}
          >
            {display}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

// ─── Financial Summary Item ────────────────────────────────────────────────────
const FinItem = ({ icon: Icon, label, value, color, delay = 0 }) => {
  const animated = useCountUp(value, 1400, 0);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <Box
      sx={{
        flex: '1 1 220px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: '18px 20px',
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.04)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          position: 'relative',
          overflow: 'hidden',
          transition: 'border-color 0.3s, box-shadow 0.3s',
          '&:hover': {
            borderColor: alpha(color, 0.5),
            boxShadow: `0 4px 24px ${alpha(color, 0.15)}`,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '15%',
            bottom: '15%',
            width: '3px',
            borderRadius: '0 3px 3px 0',
            background: color,
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            background: alpha(color, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 22, color }} />
        </Box>
        <Stack spacing={0.25}>
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Syne", sans-serif',
              fontSize: '1.05rem',
              fontWeight: 700,
              color,
              letterSpacing: '-0.01em',
            }}
          >
            UGX {animated.toLocaleString()}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

// ─── Main export: drop these two sections into your Dashboard ─────────────────
export const DashboardStatCards = ({ dashboardData }) => {
  const totalCollected = parseNumber(dashboardData.totalCollected);
  const totalDue = parseNumber(dashboardData.totalDue);
  const collectionRate = calculatePercentage(totalCollected, totalCollected + totalDue);

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);

  const cards = [
    {
      icon: People,
      label: 'Total Students',
      value: dashboardData.totalStudents,
      color: '#667eea',
      format: 'number',
    },
    {
      icon: School,
      label: 'Total Teachers',
      value: dashboardData.totalTeachers || 0,
      color: '#9c27b0',
      format: 'number',
    },
    {
      icon: AttachMoney,
      label: 'Total Collected',
      value: parseFloat(dashboardData.totalCollected) || 0,
      color: '#4caf50',
      format: 'currency',
    },
    {
      icon: TrendingDown,
      label: 'Total Due',
      value: parseFloat(dashboardData.totalDue) || 0,
      color: '#ff9800',
      format: 'currency',
    },
    {
      icon: TrendingDown,
      label: 'Total Expenses',
      value: parseFloat(dashboardData.totalExpenses) || 0,
      color: '#f44336',
      format: 'currency',
    },
    {
      icon: TrendingUp,
      label: 'Collection Rate',
      value: collectionRate,
      color: '#2196f3',
      format: 'percent',
    },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      {cards.map((c, i) => (
        <StatCard key={c.label} {...c} delay={i * 80} />
      ))}
    </Box>
  );
};

export const DashboardFinancialSummary = ({ dashboardData }) => {
  const totalBalance =
    (parseFloat(dashboardData.cashAtHand) || 0) +
    (parseFloat(dashboardData.cashInBank) || 0) +
    (parseFloat(dashboardData.cashOnMobileMoney) || 0);

  const items = [
    { icon: Money, label: 'Cash at Hand', value: parseFloat(dashboardData.cashAtHand) || 0, color: '#4caf50' },
    { icon: AccountBalance, label: 'Cash in Bank', value: parseFloat(dashboardData.cashInBank) || 0, color: '#1976d2' },
    { icon: PhoneAndroid, label: 'Mobile Money', value: parseFloat(dashboardData.cashOnMobileMoney) || 0, color: '#0288d1' },
    { icon: AttachMoney, label: 'Total Balance', value: totalBalance, color: '#f57c00' },
  ];

  return (
    <Box
      sx={{
        mb: 3,
        p: '20px 24px',
        borderRadius: '20px',
        border: '1px solid rgba(255,255,255,0.06)',
        background: 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
        <Box
          sx={{
            width: 6,
            height: 22,
            borderRadius: '3px',
            background: 'linear-gradient(180deg, #2196f3, #0d47a1)',
          }}
        />
        <Typography
          sx={{
            fontFamily: '"Syne", sans-serif',
            fontWeight: 700,
            fontSize: '1rem',
            letterSpacing: '-0.01em',
          }}
        >
          Financial Summary
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {items.map((item, i) => (
          <FinItem key={item.label} {...item} delay={i * 80} />
        ))}
      </Box>
    </Box>
  );
};
